import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Spin } from "antd";
import { AppContext } from "../../AppContext";
import { env } from "../../env/env";
import BrandTemplateApi, {
  BrandTemplateCopyContextDto,
  BrandTemplateCopyDto,
  BrandTemplateDto,
} from "../../api/BrandTemplateApi";
import { getActiveCycle } from "../../service/DesignService";
import { defaultBrandTemplateCopyName, getBrandTemplateCopyNameError } from "../../util/utils";
import "./BrandTemplateCopyModal.scss";

const JustCreatedMinutes = 10;
const ClockSkewMs = 60 * 1000;

enum Step {
  Choose,
  Name,
}

export interface BrandTemplateCopyModalProps {
  template?: BrandTemplateDto;
  onClose: () => void;
}

function shortDate(date: Date) {
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function copyStatus(copy: BrandTemplateCopyDto) {
  const created = copy.createdDate ? new Date(copy.createdDate) : null;
  const ageMs = created ? Date.now() - created.getTime() : null;
  if (ageMs !== null && ageMs > -ClockSkewMs && ageMs < JustCreatedMinutes * 60 * 1000) return "just created";
  const parts = [];
  if (copy.updatedDate) parts.push(`updated ${shortDate(new Date(copy.updatedDate))}`);
  const cycle = getActiveCycle(copy);
  if (cycle) parts.push(`${cycle.name} • ends ${cycle.endsLabel}`);
  return parts.join(" • ");
}

function openCopy(copy: BrandTemplateCopyDto) {
  window.location.href = `${env.timberContextPath}/design?id=${encodeURIComponent(copy.id)}`;
}

function createCopy(template: BrandTemplateDto, name: string) {
  window.location.href =
    `${env.timberContextPath}/design?parentId=${encodeURIComponent(template.id!)}` +
    `&fromSample=true&name=${encodeURIComponent(name)}`;
}

export default function BrandTemplateCopyModal({ template, onClose }: BrandTemplateCopyModalProps) {
  const { location } = useContext(AppContext);
  const [context, setContext] = useState<BrandTemplateCopyContextDto>();
  const [step, setStep] = useState(Step.Choose);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const takenNames = useMemo(() => new Set(context?.takenNames ?? []), [context]);

  function startNaming(loaded: BrandTemplateCopyContextDto) {
    setName(defaultBrandTemplateCopyName(template!.name, new Set(loaded.takenNames)));
    setStep(Step.Name);
  }

  useEffect(() => {
    if (!template?.id || !location?.internalId) return;
    let cancelled = false;
    setContext(undefined);
    setStep(Step.Choose);
    setSubmitting(false);
    BrandTemplateApi.getCopyContext(template.id, location.internalId)
      .catch(() => ({ templateName: template.name, copies: [], takenNames: [] } as BrandTemplateCopyContextDto))
      .then((loaded) => {
        if (cancelled) return;
        setContext(loaded);
        if (loaded.copies.length === 0) startNaming(loaded);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.id, location?.internalId]);

  const nameError = template ? getBrandTemplateCopyNameError(name, template.name, takenNames) : null;
  const copies = context?.copies ?? [];

  function renderChoose() {
    const single = copies.length === 1;
    return (
      <div className="btc-callout">
        <p className="btc-callout-title">
          {single
            ? `${location.name} already has a menu from this template`
            : `${location.name} has ${copies.length} menus from this template.`}
        </p>
        <p className="btc-callout-sub">{single ? copyStatus(copies[0]) : "Open one, or start another copy."}</p>
        {single ? (
          <Button type="primary" size="large" onClick={() => openCopy(copies[0])}>
            Open my menu
          </Button>
        ) : (
          <div className="btc-copies">
            {copies.map((copy) => (
              <button key={copy.id} type="button" className="btc-copy" onClick={() => openCopy(copy)}>
                <span className="btc-copy-name">{copy.name}</span>
                <span className="btc-copy-status">{copyStatus(copy)}</span>
              </button>
            ))}
          </div>
        )}
        <button type="button" className="btc-new-copy" onClick={() => startNaming(context!)}>
          Start a new copy instead
        </button>
      </div>
    );
  }

  function renderName() {
    return (
      <>
        <div className="btc-name-form">
          <label className="btc-name-label" htmlFor="btc-name-input">
            Name your new menu
          </label>
          <Input
            id="btc-name-input"
            autoFocus
            size="large"
            value={name}
            status={nameError ? "error" : undefined}
            onChange={(e) => setName(e.target.value)}
            onPressEnter={() => !nameError && submit()}
          />
          {nameError && <span className="btc-name-error">{nameError}</span>}
        </div>
        <div className="btc-actions">
          <Button size="large" onClick={onClose}>
            Cancel
          </Button>
          <Button size="large" type="primary" disabled={!!nameError} loading={submitting} onClick={submit}>
            Create menu
          </Button>
        </div>
      </>
    );
  }

  function submit() {
    setSubmitting(true);
    createCopy(template!, name.trim());
  }

  if (!template) return null;

  return (
    <Modal open footer={null} centered width={560} onCancel={onClose} className="brand-template-copy-modal">
      <div className="btc-header">
        <h2>{template.name}</h2>
        <p>{`Brand Template${context?.brandKitName ? ` • ${context.brandKitName}` : ""}`}</p>
      </div>
      {!context ? <Spin /> : step === Step.Choose ? renderChoose() : renderName()}
    </Modal>
  );
}
